import { useI18n, type MessageKey } from '@ecom/i18n';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useActionableOrdersCountQuery } from '../api';

import { SkIcon, type SkIconName } from '@/components/ui/SkIcon';
import { useInventoryOverviewQuery } from '@/features/inventory/api';
import { useCouponsQuery } from '@/features/promotions/api';

const COUPON_EXPIRY_WINDOW_DAYS = 7;

type AlertLevel = 'critical' | 'warning' | 'info';

interface AlertItem {
  readonly id: string;
  readonly level: AlertLevel;
  readonly icon: SkIconName;
  readonly title: string;
  readonly description: string;
  readonly href: string;
  readonly actionKey: MessageKey;
}

/**
 * « À votre attention » in the Figma design (« Souk 2.0 »): the merchant's pending work as prioritised rows — an icon
 * tile coloured by priority (critical red, warning amber, information blue), the sentence, and the contextual action
 * that opens the screen that handles it. Each row comes from an endpoint the app already uses (never invented data):
 *   - low or no stock        → useInventoryOverviewQuery() (critical as soon as a product is out of stock)
 *   - orders to process      → useActionableOrdersCountQuery() (the statuses OrderTable.tsx lets the merchant act on)
 *   - coupons ending soon    → useCouponsQuery(), filtered on `validUntil` (the only real end-of-validity field)
 * The count badge in the header is the number of rows; « À traiter » opens the full to-do screen.
 */
export function AlertsPanel(): ReactElement {
  const { t } = useI18n();
  const inventory = useInventoryOverviewQuery();
  const actionableOrders = useActionableOrdersCountQuery();
  const couponsQuery = useCouponsQuery();

  const isPending = inventory.isPending || actionableOrders.isPending || couponsQuery.isPending;

  const lowStockRows = inventory.rows.filter(
    (row) => row.stock.state === 'low_stock' || row.stock.state === 'out_of_stock',
  );
  const outOfStockCount = lowStockRows.filter((row) => row.stock.state === 'out_of_stock').length;

  const now = Date.now();
  const expiryThreshold = now + COUPON_EXPIRY_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const expiringCoupons = (couponsQuery.data ?? []).filter((coupon) => {
    if (!coupon.isActive || coupon.validUntil === null) return false;
    const expiresAt = new Date(coupon.validUntil).getTime();
    return expiresAt >= now && expiresAt <= expiryThreshold;
  });

  const items: AlertItem[] = [];

  if (actionableOrders.count > 0) {
    items.push({
      id: 'actionable-orders',
      level: 'info',
      icon: 'clock',
      title:
        actionableOrders.count === 1
          ? t('merchant.dashboard.alerts.actionableOrdersOne')
          : t('merchant.dashboard.alerts.actionableOrdersMany', {
              count: actionableOrders.count,
            }),
      description: t('merchant.dashboard.alerts.actionableOrdersDescription'),
      href: '/orders',
      actionKey: 'merchant.dashboard.alerts.actionProcess',
    });
  }

  if (lowStockRows.length > 0) {
    items.push({
      id: 'low-stock',
      level: outOfStockCount > 0 ? 'critical' : 'warning',
      icon: 'inventory',
      title:
        lowStockRows.length === 1
          ? t('merchant.dashboard.alerts.lowStockOne')
          : t('merchant.dashboard.alerts.lowStockMany', { count: lowStockRows.length }),
      description:
        outOfStockCount > 0
          ? t('merchant.dashboard.alerts.lowStockOutOfStock', { count: outOfStockCount })
          : t('merchant.dashboard.alerts.lowStockBelowThreshold'),
      href: '/inventory',
      actionKey: 'merchant.dashboard.alerts.actionCheck',
    });
  }

  if (expiringCoupons.length > 0) {
    items.push({
      id: 'expiring-coupons',
      level: 'warning',
      icon: 'coupon',
      title:
        expiringCoupons.length === 1
          ? t('merchant.dashboard.alerts.expiringCouponsOne')
          : t('merchant.dashboard.alerts.expiringCouponsMany', {
              count: expiringCoupons.length,
            }),
      description: t('merchant.dashboard.alerts.expiringCouponsDescription', {
        days: COUPON_EXPIRY_WINDOW_DAYS,
      }),
      href: '/promotions',
      actionKey: 'merchant.dashboard.alerts.actionView',
    });
  }

  // Critical first, then warnings, then information: the order the design gives them.
  const RANK: Record<AlertLevel, number> = { critical: 0, warning: 1, info: 2 };
  items.sort((a, b) => RANK[a.level] - RANK[b.level]);

  return (
    <section
      className="sk-panel sk-attention flex h-full flex-col"
      aria-labelledby="attention-title"
    >
      <div className="sk-section-head">
        <div>
          <span className="sk-eyebrow">{t('merchant.dashboard.attentionEyebrow')}</span>
          <h2 id="attention-title">{t('merchant.dashboard.attentionTitle')}</h2>
        </div>
        {items.length > 0 ? (
          <span className="sk-count-badge">
            {t('merchant.dashboard.attentionCount', { count: items.length })}
          </span>
        ) : null}
      </div>

      <div className="mt-2 flex-1">
        {isPending ? (
          <div
            className="flex flex-col gap-3 pt-2"
            aria-label={t('merchant.dashboard.alerts.checking')}
          >
            <span className="ecom-skeleton block h-12 rounded-xl" />
            <span className="ecom-skeleton block h-12 rounded-xl" />
          </div>
        ) : items.length === 0 ? (
          <p className="mt-3 flex items-start gap-2 text-sm text-inkSoft">
            <SkIcon name="check" size={16} className="mt-0.5 shrink-0 text-good" />
            {t('merchant.dashboard.alerts.allClear')}
          </p>
        ) : (
          items.map((item) => (
            <div key={item.id} className="sk-alert anim-rise" data-level={item.level}>
              <span className="sk-alert-icon">
                <SkIcon name={item.icon} />
              </span>
              <div className="min-w-0">
                <strong>{item.title}</strong>
                <p>{item.description}</p>
              </div>
              <Link
                to={item.href}
                className={
                  item.level === 'critical'
                    ? 'sk-btn sk-btn--secondary h-9'
                    : 'sk-btn sk-btn--ghost h-9'
                }
              >
                {t(item.actionKey)}
              </Link>
            </div>
          ))
        )}
      </div>

      <Link to="/todo" className="sk-btn sk-btn--ghost mt-3 self-start px-0">
        {t('merchant.nav.todo')}
        <SkIcon name="arrow" size={15} className="rtl:-scale-x-100" />
      </Link>
    </section>
  );
}
